import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersAPI, productsAPI } from '../../utils/api';
import { format } from 'date-fns';
import { ru, enUS, ka, et, hy, az } from 'date-fns/locale';
import { FaArrowLeft, FaSync, FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import '../../styles/OrderDetails.css';
import { useTranslation } from 'react-i18next';

const AdminOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  
  // Состояние для редактирования
  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const getDateLocale = () => {
    const locales = { ru, en: enUS, et, ka, hy, az };
    return locales[i18n.language] || et;
  };

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrder = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await ordersAPI.getOrder(id);
      const orderData = response.data.data;
      
      // Загружаем актуальные данные о товарах (stock может измениться)
      const productIds = orderData.items.map(item => item.product?._id).filter(Boolean);
      if (productIds.length > 0) {
        try {
          // Загружаем свежие данные о всех товарах из заказа
          const productsPromises = productIds.map(productId => 
            productsAPI.getProduct(productId).catch(err => {
              console.error(`Ошибка загрузки товара ${productId}:`, err);
              return null;
            })
          );
          const productsResponses = await Promise.all(productsPromises);
          
          // Создаем мапу актуальных данных о товарах
          const productsMap = {};
          productsResponses.forEach(res => {
            if (res && res.data.data) {
              productsMap[res.data.data._id] = res.data.data;
            }
          });
          
          // Обновляем данные о товарах в заказе
          orderData.items = orderData.items.map(item => {
            if (item.product && productsMap[item.product._id]) {
              return {
                ...item,
                product: {
                  ...item.product,
                  stockQuantity: productsMap[item.product._id].stockQuantity,
                  boxQuantity: productsMap[item.product._id].boxQuantity
                }
              };
            }
            return item;
          });
        } catch (err) {
          console.error('Ошибка загрузки актуальных данных о товарах:', err);
        }
      }
      
      setOrder(orderData);
      setNewStatus(orderData.status);
    } catch (error) {
      console.error('Ошибка загрузки заказа:', error);
      setError('Не удалось загрузить заказ');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await ordersAPI.updateStatus(order._id, {
        status: newStatus,
        comment: statusComment
      });
      alert('Статус заказа обновлен');
      loadOrder();
      setStatusComment('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Ошибка обновления статуса';
      alert(errorMessage);
    }
  };

  // Функции редактирования заказа
  const handleStartEdit = () => {
    if (order.status !== 'pending') {
      alert('Можно редактировать только заказы в статусе "' + t(`waitsForConfirmation`, 'Waits for confirmation') + '"');
      return;
    }
    setEditedItems(order.items.map(item => ({
      ...item,
      product: item.product?._id || null,
      productData: item.product || null,
      customDiscountedPrice: null // Админская кастомная цена - изначально не установлена
    })));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedItems([]);
    setSearchProducts([]);
    setSearchQuery('');
  };

  const handleSaveEdit = async () => {
    if (editedItems.length === 0) {
      alert('Заказ должен содержать хотя бы один товар');
      return;
    }

    setSaving(true);
    try {
      const itemsToSave = editedItems.map(item => ({
        product: item.product,
        quantity: item.quantity,
        unit: item.unit,
        customDiscountedPrice: item.customDiscountedPrice
      }));

      await ordersAPI.updateOrder(id, { items: itemsToSave });
      alert('Заказ успешно обновлен');
      setIsEditing(false);
      setEditedItems([]);
      loadOrder();
    } catch (error) {
      console.error('Ошибка обновления заказа:', error);
      alert(error.response?.data?.message || 'Не удалось обновить заказ');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    const updated = [...editedItems];
    updated[index].quantity = Math.max(1, parseInt(newQuantity) || 1);
    setEditedItems(updated);
  };

  const handleUpdateDiscountedPrice = (index, newPrice) => {
    const updated = [...editedItems];
    const pricePerUnit = updated[index].pricePerUnit || 0;
    const price = parseFloat(newPrice);
    
    // Проверяем что цена валидная и не больше оригинальной
    if (!isNaN(price) && price >= 0 && price <= pricePerUnit) {
      updated[index].customDiscountedPrice = price;
    } else if (newPrice === '' || newPrice === null) {
      // Если поле очищено, убираем кастомную скидку
      updated[index].customDiscountedPrice = null;
    }
    
    setEditedItems(updated);
  };

  const handleRemoveItem = (index) => {
    if (editedItems.length === 1) {
      alert('Заказ должен содержать хотя бы один товар');
      return;
    }
    const updated = editedItems.filter((_, i) => i !== index);
    setEditedItems(updated);
  };

  const handleSearchProducts = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchProducts([]);
      return;
    }

    try {
      const response = await productsAPI.getProducts({ search: query });
      setSearchProducts(response.data.data.filter(p => p.isActive));
    } catch (error) {
      console.error('Ошибка поиска товаров:', error);
    }
  };

  const handleAddProduct = (product, unit = 'piece') => {
    const newItem = {
      product: product._id,
      productData: product,
      name: product.name,
      quantity: 1,
      unit: unit,
      pricePerUnit: unit === 'box' && product.boxPrice ? product.boxPrice : product.price,
      customDiscountedPrice: null,
      boxQuantity: unit === 'box' ? product.boxQuantity : undefined
    };
    setEditedItems([...editedItems, newItem]);
    setSearchQuery('');
    setSearchProducts([]);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t(`waitsForConfirmation`, 'Waits for confirmation'),
      confirmed: 'Подтвержден',
      processing: 'В обработке',
      completed: 'Выполнен',
      cancelled: 'Отменен',
      rejected: 'Отклонен'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      rejected: 'status-rejected'
    };
    return classes[status] || '';
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'kg': return 'кг';
      case 'piece': return 'шт';
      case 'box': return 'ящ';
      default: return unit;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="error-container">
        <p>{error || 'Заказ не найден'}</p>
        <button onClick={() => navigate('/admin/orders')} className="btn-primary">
          t(`backToOrders`, 'Back to orders')
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-page">
      <button onClick={() => navigate('/admin/orders')} className="back-btn">
        <FaArrowLeft /> {t(`backToOrders`, 'Back to orders')}
      </button>

      <div className="order-details-container">
        <div className="order-details-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1>{t(`order`, 'Order')} #{order.orderNumber}</h1>
            <button 
              onClick={() => loadOrder(true)} 
              className="btn-icon"
              title="Обновить данные о наличии товаров"
              disabled={refreshing}
              style={{ 
                padding: '0.5rem 1rem',
                backgroundColor: refreshing ? '#ccc' : '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: refreshing ? 0.6 : 1
              }}
            >
              <FaSync style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> 
              {refreshing ? 'Обновление...' : 'Обновить остатки'}
            </button>
          </div>
          <div className={`order-status-large ${getStatusClass(order.status)}`}>
            {getStatusLabel(order.status)}
          </div>
        </div>

        <div className="order-info-grid">
          <div className="info-section">
            <h3>{t(`orderInfo`, 'Order info')}</h3>
            <div className="info-item">
              <strong>{t(`createdDate`, 'Created date')}:</strong>
              <span>{format(new Date(order.createdAt), 'd MMMM yyyy, HH:mm', { locale: getDateLocale() })}</span>
            </div>
            <div className="info-item">
              <strong>{t(`deliveryDate`, 'Delivery date')}:</strong>
              <span>
                {format(new Date(order.deliveryDate), 'd MMMM yyyy', { locale: getDateLocale() })}
              </span>
            </div>
            {order.notes && (
              <div className="info-item">
                <strong>Комментарий клиента:</strong>
                <span>{order.notes}</span>
              </div>
            )}
          </div>

          <div className="info-section">
            <h3>Клиент</h3>
            <div className="info-item">
              <strong>Ресторан:</strong>
              <span>{order.customer?.restaurantName}</span>
            </div>
            <div className="info-item">
              <strong>Email:</strong>
              <span>{order.customer?.email}</span>
            </div>
            <div className="info-item">
              <strong>Адрес:</strong>
              <span>
                {order.customer?.address?.street}<br />
                {order.customer?.address?.postalCode} {order.customer?.address?.city}<br />
                {order.customer?.address?.country}
              </span>
            </div>
            <div className="info-item">
              <strong>{t(`contact`, 'Contact')}:</strong>
              <span>
                {order.customer?.contactPerson?.firstName} {order.customer?.contactPerson?.lastName}<br />
                {order.customer?.contactPerson?.phone}
              </span>
            </div>
          </div>
        </div>

        <div className="order-items-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3>{t(`productsInOrder`, 'Products in order')}</h3>
            {!isEditing && order.status === 'pending' && (
              <button
                onClick={handleStartEdit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaEdit /> Редактировать заказ
              </button>
            )}
            {isEditing && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: saving ? '#ccc' : '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FaSave /> {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={saving}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FaTimes /> Отмена
                </button>
              </div>
            )}
          </div>

          {!isEditing ? (
            <>
            <div className="order-items-table">
            <div className="table-header">
              <div className="col-product">Товар</div>
              <div className="col-price">{t(`price`, 'Price')}</div>
              <div className="col-quantity">{t(`quantity`, 'Quantity')}</div>
              <div className="col-stock">На складе</div>
              <div className="col-discount">{t(`discount`, 'Discount')}</div>
              <div className="col-total">{t(`total`, 'Total')}</div>
            </div>
            {order.items.map((item) => {
              // Вычисляем необходимое количество на складе
              let requiredStock = item.quantity;
              if (item.unit === 'box') {
                requiredStock = item.quantity * (item.boxQuantity || 1);
              }
              
              // Текущее количество на складе
              const currentStock = item.product?.stockQuantity || 0;
              
              // Проверяем достаточно ли товара
              const hasEnoughStock = currentStock >= requiredStock;
              
              // Для ящиков показываем сколько максимум можно собрать
              let maxBoxes = 0;
              if (item.unit === 'box' && item.boxQuantity) {
                maxBoxes = Math.floor(currentStock / item.boxQuantity);
              }
              
              return (
                <div key={item._id} className="table-row">
                  <div className="col-product">
                    <div className="product-info">
                      <div>
                        <span>{item.name}</span>
                        {item.unit === 'box' && item.boxQuantity && (
                          <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                            (Ящик: {item.boxQuantity} шт)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-price">
                    {item.pricePerUnit.toFixed(2)} € / {getUnitLabel(item.unit)}
                  </div>
                  <div className="col-quantity">
                    {item.quantity} {getUnitLabel(item.unit)}
                    {item.unit === 'box' && item.boxQuantity && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                        = {item.quantity * item.boxQuantity} шт
                      </div>
                    )}
                  </div>
                  <div className="col-stock">
                    <div style={{ 
                      color: hasEnoughStock ? '#4caf50' : '#f44336',
                      fontWeight: 'bold'
                    }}>
                      {currentStock} шт
                    </div>
                    {item.unit === 'box' && item.boxQuantity && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                        ≈ {maxBoxes} ящ
                      </div>
                    )}
                    {!hasEnoughStock && (
                      <div style={{ fontSize: '0.75em', color: '#f44336', marginTop: '4px' }}>
                        ⚠️ Нехватка: {requiredStock - currentStock} шт
                      </div>
                    )}
                  </div>
                  <div className="col-discount">
                    {(() => {
                      if (item.discount === 0) return '-';
                      
                      if (item.discountType === 'custom_price') {
                        // Для кастомной цены вычисляем реальную скидку
                        const discountedPrice = item.totalPrice / item.quantity;
                        const discountAmount = item.pricePerUnit - discountedPrice;
                        return discountAmount > 0 ? `-${discountAmount.toFixed(2)} €` : '-';
                      }
                      
                      if (item.discountType === 'fixed') {
                        return `-${item.discount.toFixed(2)} € / ${getUnitLabel(item.product?.unit || 'piece')}`;
                      }
                      
                      return `-${item.discount}%`;
                    })()}
                  </div>
                  <div className="col-total">
                    {item.totalPrice.toFixed(2)} €
                  </div>
                </div>
              );
            })}
          </div>

          <div className="order-summary">
            <div className="summary-row">
              <span>{t(`subTotal`, 'Subtotal')}:</span>
              <span>{order.subtotal.toFixed(2)} €</span>
            </div>
            {order.totalDiscount > 0 && (
              <div className="summary-row discount-row">
                <span>{t(`discount`, 'Discount')}:</span>
                <span>-{order.totalDiscount.toFixed(2)} €</span>
              </div>
            )}
            <div className="summary-row total-row">
              <strong>{t(`total`, 'Total')}</strong>
              <strong>{order.total.toFixed(2)} €</strong>
            </div>
          </div>
          </>
          ) : (
            // Режим редактирования
            <div>
              {editedItems.map((item, index) => {
                const pricePerUnit = item.pricePerUnit || 0;
                const customDiscountedPrice = item.customDiscountedPrice;
                const discountedPrice = customDiscountedPrice !== null && customDiscountedPrice !== undefined 
                  ? customDiscountedPrice 
                  : pricePerUnit;
                const itemTotal = discountedPrice * item.quantity;
                const hasCustomDiscount = customDiscountedPrice !== null && customDiscountedPrice !== undefined;

                return (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
                    gap: '1rem',
                    padding: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.unit === 'box' && item.boxQuantity && (
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          (Ящик: {item.boxQuantity} шт)
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ fontSize: '0.85em', color: '#666' }}>{t(`quantity`, 'Quantity')}</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleUpdateQuantity(index, e.target.value)}
                        min="1"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.85em', color: '#666' }}>
                        Цена со скидкой (€)
                      </label>
                      <input
                        type="number"
                        value={customDiscountedPrice !== null && customDiscountedPrice !== undefined ? customDiscountedPrice : ''}
                        onChange={(e) => handleUpdateDiscountedPrice(index, e.target.value)}
                        min="0"
                        max={pricePerUnit}
                        step="0.01"
                        placeholder={pricePerUnit.toFixed(2)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <div style={{ fontSize: '0.75em', color: '#999', marginTop: '2px' }}>
                        Макс: {pricePerUnit.toFixed(2)} €
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666' }}>Оригинал</div>
                      <div>{pricePerUnit.toFixed(2)} €</div>
                      {hasCustomDiscount && (
                        <div style={{ fontSize: '0.85em', color: '#4caf50', fontWeight: 'bold' }}>
                          Скидка: {(pricePerUnit - discountedPrice).toFixed(2)} €
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: '0.85em', color: '#666' }}>{t(`total`, 'Total')}</div>
                      <strong style={{ color: '#2196f3' }}>{itemTotal.toFixed(2)} €</strong>
                    </div>

                    <button
                      onClick={() => handleRemoveItem(index)}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Удалить"
                    >
                      <FaTrash />
                    </button>
                  </div>
                );
              })}

              {/* Поиск и добавление товаров */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                marginTop: '1rem'
              }}>
                <h4>Добавить товар</h4>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchProducts(e.target.value)}
                  placeholder="Поиск товаров..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '0.5rem'
                  }}
                />
                {searchProducts.length > 0 && (
                  <div style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}>
                    {searchProducts.map((product) => (
                      <div key={product._id} style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong>{product.name}</strong>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            {product.price.toFixed(2)} € / {getUnitLabel(product.unit)}
                            {product.sellByBox && product.boxPrice && (
                              <> | Ящик: {product.boxPrice.toFixed(2)} € ({product.boxQuantity} шт)</>
                            )}
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#999' }}>
                            На складе: {product.stockQuantity} шт
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleAddProduct(product, 'piece')}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85em'
                            }}
                          >
                            <FaPlus /> Штучно
                          </button>
                          {product.sellByBox && product.boxPrice && (
                            <button
                              onClick={() => handleAddProduct(product, 'box')}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85em'
                              }}
                            >
                              <FaPlus /> Ящиком
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Итоговая сумма в режиме редактирования */}
              <div className="order-summary" style={{ marginTop: '1.5rem' }}>
                {(() => {
                  let subtotal = 0;
                  let totalDiscount = 0;

                  editedItems.forEach(item => {
                    const pricePerUnit = item.pricePerUnit || 0;
                    const customDiscountedPrice = item.customDiscountedPrice;
                    const discountedPrice = customDiscountedPrice !== null && customDiscountedPrice !== undefined 
                      ? customDiscountedPrice 
                      : pricePerUnit;
                    
                    subtotal += pricePerUnit * item.quantity;
                    totalDiscount += (pricePerUnit - discountedPrice) * item.quantity;
                  });

                  const total = subtotal - totalDiscount;

                  return (
                    <>
                      <div className="summary-row">
                        <span>{t(`subTotal`, 'Subtotal')}:</span>
                        <span>{subtotal.toFixed(2)} €</span>
                      </div>
                      {totalDiscount > 0 && (
                        <div className="summary-row discount-row">
                          <span>{t(`discount`, 'Discount')}:</span>
                          <span>-{totalDiscount.toFixed(2)} €</span>
                        </div>
                      )}
                      <div className="summary-row total-row">
                        <strong>{t(`total`, 'Total')}:</strong>
                        <strong>{total.toFixed(2)} €</strong>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        <div className="status-update-section">
          <h3>Изменить статус заказа</h3>
          
          {/* Проверка наличия товаров */}
          {(() => {
            const insufficientItems = order.items.filter(item => {
              const requiredStock = item.unit === 'box' 
                ? item.quantity * (item.boxQuantity || 1)
                : item.quantity;
              const currentStock = item.product?.stockQuantity || 0;
              return currentStock < requiredStock;
            });
            
            const canConfirm = insufficientItems.length === 0;
            const isConfirming = order.status === 'pending' && newStatus === 'confirmed';
            
            return (
              <>
                {insufficientItems.length > 0 && (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    padding: '15px',
                    marginBottom: '15px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>
                      ⚠️ Внимание: недостаточно товаров на складе
                    </h4>
                    <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404' }}>
                      {insufficientItems.map((item, idx) => {
                        const requiredStock = item.unit === 'box' 
                          ? item.quantity * (item.boxQuantity || 1)
                          : item.quantity;
                        const currentStock = item.product?.stockQuantity || 0;
                        const shortage = requiredStock - currentStock;
                        
                        return (
                          <li key={idx}>
                            <strong>{item.name}:</strong> нужно {requiredStock} шт, 
                            на складе {currentStock} шт (нехватка: {shortage} шт)
                          </li>
                        );
                      })}
                    </ul>
                    <p style={{ margin: '10px 0 0 0', fontSize: '0.9em', color: '#856404' }}>
                      При подтверждении заказа остатки на складе уйдут в минус. Убедитесь в возможности пополнения запасов.
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleStatusUpdate} className="status-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Новый статус</label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                      >
                        <option value="pending">{t(`waitsForConfirmation`, 'Waits for confirmation')}</option>
                        <option value="confirmed">Подтвержден</option>
                        <option value="processing">В обработке</option>
                        <option value="completed">Выполнен</option>
                        <option value="cancelled">Отменен</option>
                        <option value="rejected">Отклонен</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Комментарий</label>
                      <input
                        type="text"
                        value={statusComment}
                        onChange={(e) => setStatusComment(e.target.value)}
                        placeholder="Опционально"
                      />
                    </div>
                  </div>
                  <button 
                    type="submit" 
                    className="btn-primary"
                  >
                    Обновить статус
                  </button>
                  {isConfirming && !canConfirm && (
                    <div style={{ 
                      marginTop: '10px', 
                      color: '#ff9800',
                      fontSize: '0.9em',
                      padding: '10px',
                      backgroundColor: '#fff3e0',
                      borderRadius: '4px',
                      border: '1px solid #ff9800'
                    }}>
                      ⚠️ Внимание: на складе недостаточно товаров. При подтверждении остатки могут уйти в минус.
                    </div>
                  )}
                </form>
              </>
            );
          })()}
        </div>

        {order.statusHistory && order.statusHistory.length > 0 && (
          <div className="status-history-section">
            <h3>{t(`statusHistory`, 'Status history')}</h3>
            <div className="status-history">
              {order.statusHistory.slice().reverse().map((history, index) => (
                <div key={index} className="history-item">
                  <div className="history-date">
                    {format(new Date(history.changedAt), 'd MMMM yyyy, HH:mm', { locale: getDateLocale() })}
                  </div>
                  <div className="history-status">
                    <strong>{getStatusLabel(history.status)}</strong>
                  </div>
                  {history.comment && (
                    <div className="history-comment">
                      {(() => {
                        // Если комментарий - это автоматически сгенерированный ключ изменения статуса
                        if (history.comment.startsWith('statusChangedFrom_')) {
                          // Извлекаем старый и новый статусы из ключа
                          const parts = history.comment.replace('statusChangedFrom_', '').split('_to_');
                          if (parts.length === 2) {
                            const oldStatus = getStatusLabel(parts[0]);
                            const newStatus = getStatusLabel(parts[1]);
                            return `${t('statusChanged', 'Статус изменен')}: ${oldStatus} → ${newStatus}`;
                          }
                        }
                        // Иначе пытаемся перевести как обычный ключ
                        return t(history.comment, history.comment);
                      })()}
                    </div>
                  )}
                  {history.changedBy && (
                    <div className="history-user">
                      {history.changedBy.restaurantName || history.changedBy.email}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
};


export default AdminOrderDetails;

